import type { ChangeEvent, FC, FormEvent, ReactNode } from 'react'

import { useEffect, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import { isValidEmail, isValidPhone } from '@/lib/validation'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { Button, buttonVariants } from '@/components/ui/button'
import { Seo } from '@/components/generals/seo'
import { Layout } from '@/components/generals/layout'
import Container from '@/components/generals/container'
import WaveLines from '@/components/generals/wave-lines'
import FormField, { describedBy } from '@/components/generals/form-field'
import { contactTopics, directionsUrl, storeInfo } from '@/constants/site'

import {
    ArrowRight,
    ChevronDown,
    CircleAlert,
    CircleCheck,
    Clock,
    LoaderCircle,
    MapPin,
    Navigation,
    Phone
} from 'lucide-react'

type FormState = {
    name: string
    email: string
    phone: string
    topic: string
    message: string
    company: string
}

type FieldName = 'name' | 'email' | 'phone' | 'message'

const EMPTY_FORM: FormState = {
    name: '',
    email: '',
    phone: '',
    topic: contactTopics[0].value,
    message: '',
    company: ''
}

const FIELD_ORDER: Array<FieldName> = ['name', 'email', 'phone', 'message']

const validate = (form: FormState): Partial<Record<FieldName, string>> => {
    const errors: Partial<Record<FieldName, string>> = {}

    if (form.name.trim().length < 2) errors.name = "Enter your name."
    if (!isValidEmail(form.email.trim())) errors.email = "Enter a valid email address."
    if (form.phone.trim() !== '' && !isValidPhone(form.phone.trim())) errors.phone = "Check the phone number, or leave it empty."
    if (form.message.trim().length < 10) errors.message = "Tell us a little more, at least a sentence."

    return errors
}

const Contact: FC = (): ReactNode => {
    const router = useRouter()
    const [form, setForm] = useState<FormState>(EMPTY_FORM)
    const [errors, setErrors] = useState<Partial<Record<FieldName, string>>>({})
    const [formError, setFormError] = useState<string | null>(null)
    const [submitting, setSubmitting] = useState(false)
    const [sent, setSent] = useState(false)
    const formRef = useRef<HTMLFormElement>(null)

    useEffect(() => {
        if (!router.isReady) return

        const topic = typeof router.query.topic === 'string' ? router.query.topic : ''

        if (contactTopics.some(entry => entry.value === topic)) {
            setForm(current => ({ ...current, topic }))
        }
    }, [router.isReady, router.query.topic])

    const update = (field: keyof FormState) => (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        const value = event.target.value

        setForm(current => ({ ...current, [field]: value }))

        if (field in errors) setErrors(current => ({ ...current, [field]: undefined }))
    }

    const submit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()
        setFormError(null)

        const found = validate(form)

        if (Object.keys(found).length > 0) {
            setErrors(found)

            const first = FIELD_ORDER.find(field => found[field])

            if (first) formRef.current?.querySelector<HTMLElement>(`#contact-${first}`)?.focus()

            return
        }

        setSubmitting(true)

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })

            const data = await response.json().catch(() => null)

            if (!response.ok) {
                if (data?.errors) setErrors(data.errors)

                setFormError(typeof data?.message === 'string' ? data.message : "Something went wrong. Please try again or call the shop.")

                return
            }

            setSent(true)
            setForm(EMPTY_FORM)
        }
        catch {
            setFormError("We could not reach the shop. Check your connection and try again.")
        }
        finally {
            setSubmitting(false)
        }
    }

    return (
        <Layout>
            <Seo
                title='Contact'
                description="Find the shop, check opening hours or send us a question about gear, repairs or a pickup order."
            />

            <section aria-labelledby='contact-title' className='relative overflow-hidden border-b border-border bg-lagoon/40'>
                <WaveLines className='absolute inset-x-0 bottom-0 h-40 w-full text-primary/10' lines={6} />

                <Container className='relative py-12 sm:py-16'>
                    <p className='text-xs font-bold tracking-[0.18em] text-tide uppercase'>
                        Visit or get in touch
                    </p>

                    <h1 id='contact-title' className='mt-3 max-w-3xl text-4xl font-extrabold tracking-tight sm:text-5xl'>
                        Swing by on your way out, or drop us a line.
                    </h1>

                    <p className='mt-4 max-w-2xl text-lg leading-relaxed text-foreground/75'>
                        Ask about stock, book a reel service or check on a pickup order. We answer messages between customers, usually the same day.
                    </p>
                </Container>
            </section>

            <Container className='grid gap-10 py-12 sm:py-16 lg:grid-cols-12 lg:gap-12'>
                <aside aria-label='Shop details' className='space-y-4 lg:col-span-5'>
                    <div className='rounded-3xl bg-card p-6 ring-1 ring-border sm:p-7'>
                        <h2 className='flex items-center gap-2 text-sm font-bold tracking-wide text-tide uppercase'>
                            <MapPin className='size-4' />
                            Find us
                        </h2>

                        <address className='mt-3 text-lg leading-relaxed not-italic'>
                            {storeInfo.street}
                            <br />
                            {storeInfo.city}
                        </address>

                        <a
                            href={directionsUrl}
                            target='_blank'
                            rel='noopener noreferrer'
                            className={`${buttonVariants({ variant: 'outline', size: 'sm' })} mt-4`}
                        >
                            <Navigation />
                            Get directions
                        </a>
                    </div>

                    <div className='rounded-3xl bg-card p-6 ring-1 ring-border sm:p-7'>
                        <h2 className='flex items-center gap-2 text-sm font-bold tracking-wide text-tide uppercase'>
                            <Phone className='size-4' />
                            Call the shop
                        </h2>

                        <a href={storeInfo.phoneHref} className='mt-3 block font-heading text-2xl font-extrabold underline-offset-4 hover:underline'>
                            {storeInfo.phone}
                        </a>

                        <p className='mt-1 text-sm text-muted-foreground'>
                            The quickest way to check if something is in stock today.
                        </p>
                    </div>

                    <div className='rounded-3xl bg-card p-6 ring-1 ring-border sm:p-7'>
                        <h2 className='flex items-center gap-2 text-sm font-bold tracking-wide text-tide uppercase'>
                            <Clock className='size-4' />
                            Opening hours
                        </h2>

                        <dl className='mt-3 divide-y divide-border'>
                            {storeInfo.hours.map(entry => (
                                <div key={entry.days} className='flex items-center justify-between gap-4 py-2.5 last:pb-0'>
                                    <dt className='text-muted-foreground'>
                                        {entry.days}
                                    </dt>

                                    <dd className='font-semibold'>
                                        {entry.time}
                                    </dd>
                                </div>
                            ))}
                        </dl>
                    </div>
                </aside>

                <section aria-labelledby='message-title' className='lg:col-span-7'>
                    <div className='rounded-[2rem] bg-card p-6 ring-1 ring-border sm:p-10'>
                        {sent
                            ? (
                                <div className='flex flex-col items-center py-10 text-center' role='status'>
                                    <span className='flex size-16 items-center justify-center rounded-full bg-lagoon text-lagoon-foreground animate-in zoom-in-50 duration-500'>
                                        <CircleCheck className='size-8' strokeWidth={2} />
                                    </span>

                                    <h2 id='message-title' className='mt-6 text-3xl font-extrabold tracking-tight'>
                                        Message sent
                                    </h2>

                                    <p className='mt-3 max-w-md text-muted-foreground'>
                                        Thanks for getting in touch. We will reply to your email as soon as we are off the shop floor.
                                    </p>

                                    <Button variant='outline' className='mt-8' onClick={() => setSent(false)}>
                                        Send another message
                                    </Button>
                                </div>
                            )
                            : (
                                <>
                                    <h2 id='message-title' className='text-2xl font-extrabold tracking-tight sm:text-3xl'>
                                        Send us a message
                                    </h2>

                                    <p className='mt-2 text-muted-foreground'>
                                        All fields are required unless marked optional.
                                    </p>

                                    <form ref={formRef} noValidate onSubmit={submit} className='mt-8 space-y-5'>
                                        <div className='grid gap-5 sm:grid-cols-2'>
                                            <FormField id='contact-name' label='Your name' error={errors.name}>
                                                <Input
                                                    id='contact-name'
                                                    name='name'
                                                    autoComplete='name'
                                                    value={form.name}
                                                    onChange={update('name')}
                                                    aria-invalid={Boolean(errors.name)}
                                                    aria-describedby={describedBy('contact-name', errors.name)}
                                                />
                                            </FormField>

                                            <FormField id='contact-email' label='Email' error={errors.email}>
                                                <Input
                                                    id='contact-email'
                                                    name='email'
                                                    type='email'
                                                    autoComplete='email'
                                                    inputMode='email'
                                                    value={form.email}
                                                    onChange={update('email')}
                                                    aria-invalid={Boolean(errors.email)}
                                                    aria-describedby={describedBy('contact-email', errors.email)}
                                                />
                                            </FormField>
                                        </div>

                                        <div className='grid gap-5 sm:grid-cols-2'>
                                            <FormField id='contact-phone' label='Phone' optional error={errors.phone}>
                                                <Input
                                                    id='contact-phone'
                                                    name='phone'
                                                    type='tel'
                                                    autoComplete='tel'
                                                    inputMode='tel'
                                                    value={form.phone}
                                                    onChange={update('phone')}
                                                    aria-invalid={Boolean(errors.phone)}
                                                    aria-describedby={describedBy('contact-phone', errors.phone)}
                                                />
                                            </FormField>

                                            <FormField id='contact-topic' label='What is it about?'>
                                                <div className='relative'>
                                                    <select
                                                        id='contact-topic'
                                                        name='topic'
                                                        value={form.topic}
                                                        onChange={update('topic')}
                                                        className='h-11 w-full appearance-none rounded-xl border border-input bg-card pr-10 pl-3.5 text-base outline-none focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/40 md:text-sm'
                                                    >
                                                        {contactTopics.map(topic => (
                                                            <option key={topic.value} value={topic.value}>
                                                                {topic.label}
                                                            </option>
                                                        ))}
                                                    </select>

                                                    <ChevronDown className='pointer-events-none absolute top-1/2 right-3.5 size-4 -translate-y-1/2 text-muted-foreground' />
                                                </div>
                                            </FormField>
                                        </div>

                                        <FormField id='contact-message' label='Message' error={errors.message}>
                                            <Textarea
                                                id='contact-message'
                                                name='message'
                                                rows={6}
                                                maxLength={2000}
                                                value={form.message}
                                                onChange={update('message')}
                                                placeholder='What are you fishing for, and how can we help?'
                                                aria-invalid={Boolean(errors.message)}
                                                aria-describedby={describedBy('contact-message', errors.message)}
                                            />
                                        </FormField>

                                        <div aria-hidden='true' className='absolute -left-[9999px] h-0 w-0 overflow-hidden'>
                                            <label htmlFor='contact-company'>
                                                Company
                                            </label>

                                            <input
                                                id='contact-company'
                                                name='company'
                                                tabIndex={-1}
                                                autoComplete='off'
                                                value={form.company}
                                                onChange={update('company')}
                                            />
                                        </div>

                                        {formError && (
                                            <p role='alert' className='flex items-start gap-2 rounded-xl bg-destructive/10 px-3 py-2.5 text-sm font-medium text-destructive'>
                                                <CircleAlert className='mt-0.5 size-4 shrink-0' />
                                                {formError}
                                            </p>
                                        )}

                                        <Button type='submit' size='lg' variant='accent' disabled={submitting} className='w-full sm:w-auto'>
                                            {submitting
                                                ? (
                                                    <>
                                                        <LoaderCircle className='size-5 animate-spin' />
                                                        Sending
                                                    </>
                                                )
                                                : (
                                                    <>
                                                        Send message
                                                        <ArrowRight />
                                                    </>
                                                )}
                                        </Button>
                                    </form>
                                </>
                            )}
                    </div>
                </section>
            </Container>
        </Layout>
    )
}

export default Contact
